import React, { useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from './ui/Table';
import { Button } from './ui/Button';
import { Input } from './ui/Input';
import { formatCurrency } from '../lib/utils';
import { Edit2, Save, X, Users, Plus, Layers } from 'lucide-react';
import { MOCK_USERS, INITIAL_CDS_TIERS } from '../data/mockData';
export function CDSTiers() {
  const [tiers, setTiers] = useState(INITIAL_CDS_TIERS);
  const [editingTierId, setEditingTierId] = useState<string | null>(null);
  const [editForm, setEditForm] = useState<{
    min: number;
    max: number | null;
    amount: number;
  } | null>(null);
  const cdsUsers = MOCK_USERS.filter(u => u.model === 'CDS');
  const handleEdit = (tier: (typeof INITIAL_CDS_TIERS)[0]) => {
    setEditingTierId(tier.id);
    setEditForm({
      min: tier.min,
      max: tier.max,
      amount: tier.amount
    });
  };
  const handleCancel = () => {
    setEditingTierId(null);
    setEditForm(null);
  };
  const handleSave = (id: string) => {
    if (!editForm) return;
    setTiers(tiers.map(t => t.id === id ? {
      ...t,
      ...editForm
    } : t));
    setEditingTierId(null);
    setEditForm(null);
  };
  return <div className="space-y-6" data-id="element-43">
      <div className="flex items-center justify-between" data-id="element-44">
        <div data-id="element-45">
          <h2 className="text-2xl font-bold tracking-tight text-slate-900" data-id="element-46">
            CDS Commission Tiers
          </h2>
          <p className="text-slate-500" data-id="element-47">
            Manage flat-fee tiers based on funded loan volume for CDS users.
          </p>
        </div>
      </div>

      <div className="grid gap-6 lg:grid-cols-12" data-id="element-48">
        {/* Left: Tier Structure (Takes up 5 columns on large screens) */}
        <Card className="lg:col-span-5 shadow-sm border-slate-200/60 h-fit" data-id="element-49">
          <CardHeader className="flex flex-row items-center justify-between bg-white rounded-t-xl border-b border-slate-100 pb-4" data-id="element-50">
            <div data-id="element-51">
              <CardTitle className="flex items-center" data-id="element-52">
                <Layers className="mr-2 h-5 w-5 text-blue-500" data-id="element-53" />
                Tier Structure
              </CardTitle>
              <CardDescription data-id="element-54">
                Global tier settings for all CDS users
              </CardDescription>
            </div>
            <Button size="sm" variant="outline" className="h-8 shadow-sm" data-id="element-55">
              <Plus className="mr-2 h-4 w-4" data-id="element-56" /> Add Tier
            </Button>
          </CardHeader>
          <CardContent className="p-0" data-id="element-57">
            <Table data-id="element-58">
              <TableHeader className="bg-slate-50/50" data-id="element-59">
                <TableRow className="hover:bg-transparent" data-id="element-60">
                  <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-61">
                    Volume Range (Files)
                  </TableHead>
                  <TableHead className="text-right font-semibold text-slate-600" data-id="element-62">
                    Per Loan Amount
                  </TableHead>
                  <TableHead className="w-[80px] pr-6" data-id="element-63"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-64">
                {tiers.map(tier => {
                const isEditing = editingTierId === tier.id;
                return <TableRow key={tier.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-65">
                      <TableCell className="font-medium pl-6 py-4" data-id="element-66">
                        {isEditing && editForm ? <div className="flex items-center space-x-2" data-id="element-67">
                            <Input type="number" className="w-16 h-8 text-center font-semibold" value={editForm.min} onChange={e => setEditForm({
                        ...editForm,
                        min: parseInt(e.target.value) || 0
                      })} data-id="element-68" />
                            <span className="text-slate-400 font-medium" data-id="element-69">
                              to
                            </span>
                            <Input type="number" className="w-16 h-8 text-center font-semibold" value={editForm.max || ''} placeholder="+" onChange={e => setEditForm({
                        ...editForm,
                        max: e.target.value ? parseInt(e.target.value) : null
                      })} data-id="element-70" />
                          </div> : <span className="text-slate-700 font-semibold" data-id="element-71">
                            {tier.min} {tier.max ? `- ${tier.max}` : '+'}{' '}
                            <span className="text-slate-400 font-normal ml-1" data-id="element-72">
                              Loans
                            </span>
                          </span>}
                      </TableCell>
                      <TableCell className="text-right" data-id="element-73">
                        {isEditing && editForm ? <div className="flex items-center justify-end" data-id="element-74">
                            <span className="mr-2 text-slate-400 font-medium" data-id="element-75">
                              $
                            </span>
                            <Input type="number" className="w-24 h-8 text-right font-semibold" value={editForm.amount} onChange={e => setEditForm({
                        ...editForm,
                        amount: parseInt(e.target.value) || 0
                      })} data-id="element-76" />
                          </div> : <span className="font-bold text-slate-900 text-base" data-id="element-77">
                            {formatCurrency(tier.amount)}
                          </span>}
                      </TableCell>
                      <TableCell className="text-right pr-6" data-id="element-78">
                        {isEditing ? <div className="flex items-center justify-end space-x-1" data-id="element-79">
                            <Button size="icon" variant="ghost" onClick={() => handleSave(tier.id)} className="h-8 w-8 text-blue-600 hover:text-blue-700 hover:bg-blue-50" data-id="element-80">
                              <Save className="h-4 w-4" data-id="element-81" />
                            </Button>
                            <Button size="icon" variant="ghost" onClick={handleCancel} className="h-8 w-8 text-slate-400 hover:text-slate-600" data-id="element-82">
                              <X className="h-4 w-4" data-id="element-83" />
                            </Button>
                          </div> : <Button size="icon" variant="ghost" onClick={() => handleEdit(tier)} className="h-8 w-8 text-slate-400 hover:text-slate-600" data-id="element-84">
                            <Edit2 className="h-4 w-4" data-id="element-85" />
                          </Button>}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Right: Active Users (Takes up 7 columns on large screens) */}
        <Card className="lg:col-span-7 flex flex-col h-[600px] shadow-sm border-slate-200/60" data-id="element-86">
          <CardHeader className="shrink-0 bg-white rounded-t-xl border-b border-slate-100 pb-4" data-id="element-87">
            <CardTitle className="flex items-center" data-id="element-88">
              <Users className="mr-2 h-5 w-5 text-indigo-500" data-id="element-89" />
              Active CDS Users ({cdsUsers.length})
            </CardTitle>
            <CardDescription data-id="element-90">
              Current MTD performance for CDS designated users
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0" data-id="element-91">
            <Table data-id="element-92">
              <TableHeader className="sticky top-0 bg-slate-50/90 backdrop-blur-sm shadow-sm z-10 border-b border-slate-200" data-id="element-93">
                <TableRow className="hover:bg-transparent" data-id="element-94">
                  <TableHead className="pl-6 font-semibold text-slate-600" data-id="element-95">
                    User
                  </TableHead>
                  <TableHead className="text-right font-semibold text-slate-600" data-id="element-96">
                    Funded (MTD)
                  </TableHead>
                  <TableHead className="text-right pr-6 font-semibold text-slate-600" data-id="element-97">
                    Est. Commission
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody data-id="element-98">
                {cdsUsers.map(user => {
                const currentTier = tiers.find(t => user.fundedMTD >= t.min && (t.max === null || user.fundedMTD <= t.max));
                const estimatedCommission = currentTier ? user.fundedMTD * currentTier.amount : 0;
                return <TableRow key={user.id} className="hover:bg-slate-50/50 transition-colors" data-id="element-99">
                      <TableCell className="font-medium pl-6 py-3" data-id="element-100">
                        <div className="flex items-center gap-4" data-id="element-101">
                          <img src={user.avatar} alt={user.name} className="h-9 w-9 rounded-full bg-slate-200 shadow-sm border border-slate-100" data-id="element-102" />
                          <div data-id="element-103">
                            <div className="text-sm font-semibold text-slate-900" data-id="element-104">
                              {user.name}
                            </div>
                            <div className="text-xs text-slate-500 font-medium" data-id="element-105">
                              {user.title}
                            </div>
                          </div>
                        </div>
                      </TableCell>
                      <TableCell className="text-right" data-id="element-106">
                        <div className="flex flex-col items-end" data-id="element-107">
                          <span className="font-bold text-slate-700 text-base" data-id="element-108">
                            {user.fundedMTD}
                          </span>
                          {currentTier && <span className="text-xs font-medium text-slate-400 mt-0.5" data-id="element-109">
                              Tier:{' '}
                              <span className="text-slate-500" data-id="element-110">
                                {formatCurrency(currentTier.amount)}/ea
                              </span>
                            </span>}
                        </div>
                      </TableCell>
                      <TableCell className="text-right font-bold pr-6 text-slate-900 text-base" data-id="element-111">
                        {formatCurrency(estimatedCommission)}
                      </TableCell>
                    </TableRow>;
              })}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </div>;
}